import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LogOut, Circle } from 'lucide-react';

export default function Sidebar({ brand, groups, badge }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = () => { logout(); nav('/'); };

  return (
    <aside className="sidebar-dark hidden md:flex md:flex-col w-[248px] bg-slate-950 border-r border-slate-900/60 text-slate-300 h-screen sticky top-0">
      {/* Brand */}
      <div className="px-5 pt-5 pb-4 flex items-center gap-2.5">
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-teal-500 to-teal-700 text-white grid place-items-center font-bold text-[13px] tracking-tight shadow-lg shadow-teal-900/30">SE</div>
        <div className="min-w-0">
          <div className="font-semibold text-white leading-tight text-[14px] tracking-tight">StayEasy</div>
          <div className="text-[10px] text-slate-500 leading-tight uppercase tracking-[0.09em] font-medium mt-0.5">{badge}</div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-2 px-3 space-y-5">
        {groups.map((g) => (
          <div key={g.label}>
            <div className="px-2.5 mb-1.5 text-[10px] uppercase tracking-[0.09em] text-slate-500 font-semibold">{g.label}</div>
            <div className="space-y-[2px]">
              {g.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `nav-link group flex items-center gap-3 px-2.5 py-[7px] rounded-lg text-[13px] font-medium ${
                      isActive
                        ? 'bg-white/[0.06] text-white shadow-[inset_1px_0_0_0_rgba(20,184,166,0.7)]'
                        : 'text-slate-400 hover:bg-white/[0.04] hover:text-white'
                    }`
                  }
                >
                  <item.icon className="h-[16px] w-[16px] flex-shrink-0 opacity-90" strokeWidth={1.75} />
                  <span className="truncate tracking-tight">{item.label}</span>
                  {item.badge ? (
                    <span className="ml-auto text-[10px] bg-teal-500/15 text-teal-300 px-1.5 py-[1px] rounded-full font-semibold tabular-nums">
                      {item.badge}
                    </span>
                  ) : null}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User */}
      <div className="border-t border-slate-900/80 p-3">
        <div className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-white/[0.04] transition-colors">
          <img src={user?.avatar} className="h-9 w-9 rounded-full object-cover ring-1 ring-white/10" alt="" />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium text-white truncate tracking-tight">{user?.name}</div>
            <div className="text-[10.5px] text-slate-500 truncate flex items-center gap-1 mt-0.5">
              <Circle className="h-[6px] w-[6px] fill-emerald-400 text-emerald-400" />
              <span className="capitalize">{user?.role?.replace('_', ' ')}</span>
            </div>
          </div>
          <button
            onClick={handleLogout}
            title="Sign out"
            className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.08] transition-colors"
          >
            <LogOut className="h-[15px] w-[15px]" strokeWidth={1.75} />
          </button>
        </div>
      </div>
    </aside>
  );
}
